import React from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";
import { useTranslation, translate, setLocale } from "@/lib/i18n";
import {
  methodologyShortLabel,
  conflictsLabel,
} from "@/lib/researchTransparencyCopy";

/**
 * Slice C — Central Research Transparency page (WS-7 / WS-1).
 *
 * Route-driven language contract (following the existing LegalPage
 * convention):
 *
 *   * `<Route path="/en/research-transparency"
 *       element={<ResearchTransparency language="en" />} />`
 *     ALWAYS renders in English regardless of the persisted locale.
 *
 *   * `<Route path="/de/research-transparenz"
 *       element={<ResearchTransparency language="de" />} />`
 *     ALWAYS renders in German regardless of the persisted locale.
 *
 *   * `<Route path="/research-transparency"
 *       element={<ResearchTransparency />} />`
 *     Follows the current application-wide language preference
 *     (`useTranslation()` reactive locale).
 *
 * The `language` prop wins over any stored preference; it does NOT
 * mutate the persisted locale.  A cross-language link at the top of
 * the page navigates to the sibling route (EN ⇄ DE) and only there is
 * the persisted locale synchronised — via `setLocale` on click — so the
 * rest of the app follows the user's explicit language choice.
 */

// Baseline Fair Value component weights (verified against
// `poc/valuation_engine.py::WEIGHTS`).
const FV_WEIGHTS = [
  { key: "dcf",            pct: "35 %" },
  { key: "pe",             pct: "20 %" },
  { key: "fcf",            pct: "15 %" },
  { key: "profitability",  pct: "10 %" },
  { key: "rev_growth",     pct: "10 %" },
  { key: "eps_growth",     pct: "5 %"  },
  { key: "bs",             pct: "5 %"  },
];

// AI Opportunity Score baseline weights.
const AIS_WEIGHTS = [
  { key: "valuation", pct: "40 %" },
  { key: "quality",   pct: "25 %" },
  { key: "growth",    pct: "20 %" },
  { key: "safety",    pct: "15 %" },
];

const DATA_FIELDS = [
  { key: "data_as_of" },
  { key: "market_price" },
  { key: "valuation" },
  { key: "generated" },
  { key: "quality" },
  { key: "stale" },
  { key: "estimated" },
  { key: "unavailable" },
  { key: "provider" },
  { key: "sources" },
];

const FIXED_ASSUMPTIONS = [
  "assump_fixed_dcf_horizon",
  "assump_fixed_growth_cap",
  "assump_fixed_discount",
  "assump_fixed_terminal",
  "assump_fixed_band",
  "assump_fixed_caps",
];

// Sibling routes for the cross-language switch on this page.
const SIBLING_ROUTE = { en: "/de/research-transparenz", de: "/en/research-transparency" };
// Canonical Financial Disclaimer routes are language-scoped.
const DISCLAIMER_ROUTE = { en: "/en/disclaimer", de: "/de/disclaimer" };

/**
 * @param {{ language?: "en"|"de" }} props - when omitted the page follows
 *   the current app-wide language preference (canonical route only).
 */
export default function ResearchTransparency({ language }) {
  // Reactive preference locale (used only for the canonical route).
  const { locale: prefLocale } = useTranslation();
  // Route-declared language wins; otherwise honour the preference.
  const lang = language === "en" || language === "de" ? language : prefLocale;
  // Bind translation lookups to the resolved language so `/en/...`
  // stays English even when localStorage holds "de", and vice versa.
  const t = React.useCallback((key, vars) => translate(key, vars, lang), [lang]);

  const V1 = "stockaily.methodology.v1";
  const disclaimerHref = DISCLAIMER_ROUTE[lang] || DISCLAIMER_ROUTE.en;
  const siblingHref = SIBLING_ROUTE[lang] || null;
  const siblingLabel = lang === "de" ? "English" : "Deutsch";
  const siblingLang = lang === "de" ? "en" : "de";

  // When the user explicitly follows the cross-language link on this
  // page, mirror the choice into the persisted app locale so the rest
  // of the app follows the user's explicit preference. This is the
  // ONLY place on the page that mutates the stored locale.
  const onSiblingClick = React.useCallback(() => {
    setLocale(siblingLang);
  }, [siblingLang]);

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      lang={lang}
      data-testid="research-transparency-page"
      data-rt-lang={lang}
      data-rt-route-language={language || "preference"}
    >
      <NavBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <header className="mb-10">
          <div className="flex items-start justify-between gap-4">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight"
              data-testid="rt-page-title"
            >
              {t("research_transparency.page_title")}
            </h1>
            {siblingHref ? (
              <Link
                to={siblingHref}
                onClick={onSiblingClick}
                className="mt-2 shrink-0 text-sm text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded px-1 py-0.5"
                data-testid="rt-language-switch"
                data-rt-language-switch-target={siblingLang}
              >
                {siblingLabel}
              </Link>
            ) : null}
          </div>
          <p
            className="mt-4 text-base sm:text-lg text-muted-foreground max-w-3xl"
            data-testid="rt-page-intro"
          >
            {t("research_transparency.page_intro")}
          </p>
          <div
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-border/60 bg-secondary/30 px-3 py-1 text-[11px] uppercase tracking-wider text-muted-foreground"
            data-testid="rt-methodology-badge"
          >
            <span>{t("research_transparency.methodology_label")}:</span>
            <span className="text-foreground">
              {methodologyShortLabel(t, V1)}
            </span>
          </div>
        </header>

        {/* 1. What Stockaily provides */}
        <Section id="what-we-provide" title={t("research_transparency.section_what_we_provide")}>
          <p data-testid="rt-what-body">{t("research_transparency.what_body")}</p>
        </Section>

        {/* 2. How Fair Value is calculated */}
        <Section id="fair-value" title={t("research_transparency.section_fair_value")}>
          <p data-testid="rt-fv-intro">{t("research_transparency.fv_intro")}</p>

          <SubHead>{t("research_transparency.fv_weights_title")}</SubHead>
          <ul
            className="rounded-xl border border-border/60 bg-secondary/20 divide-y divide-border/50"
            data-testid="rt-fv-weights"
          >
            {FV_WEIGHTS.map((row) => (
              <li
                key={row.key}
                className="flex items-center justify-between px-4 py-2.5 text-sm"
                data-testid={`rt-fv-weight-${row.key}`}
              >
                <span>{t(`research_transparency.fv_weight_${row.key}`)}</span>
                <span
                  className="font-mono tabular-nums text-foreground"
                  data-testid={`rt-fv-weight-${row.key}-pct`}
                >
                  {row.pct}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="rt-fv-weights-note">
            {t("research_transparency.fv_weights_note")}
          </p>

          <SubHead>{t("research_transparency.fv_fallbacks_title")}</SubHead>
          <ul className="list-disc pl-5 space-y-2 text-sm" data-testid="rt-fv-fallbacks">
            <li>{t("research_transparency.fv_fallback_dcf")}</li>
            <li>{t("research_transparency.fv_fallback_missing")}</li>
            <li>{t("research_transparency.fv_fallback_extreme")}</li>
          </ul>

          <SubHead>{t("research_transparency.fv_range_title")}</SubHead>
          <p data-testid="rt-fv-range">{t("research_transparency.fv_range_body")}</p>

          <SubHead>{t("research_transparency.fv_sector_ref_title")}</SubHead>
          <p
            className="rounded-lg border border-border/60 bg-secondary/20 px-4 py-3 text-sm"
            data-testid="rt-fv-sector-ref"
          >
            {t("research_transparency.fv_sector_ref_body")}
          </p>
        </Section>

        {/* 3. How the AI Opportunity Score is calculated */}
        <Section id="ai-opportunity-score" title={t("research_transparency.section_ai_score")}>
          <p data-testid="rt-ais-intro">{t("research_transparency.ais_intro")}</p>

          <SubHead>{t("research_transparency.ais_weights_title")}</SubHead>
          <ul
            className="rounded-xl border border-border/60 bg-secondary/20 divide-y divide-border/50"
            data-testid="rt-ais-weights"
          >
            {AIS_WEIGHTS.map((row) => (
              <li
                key={row.key}
                className="flex items-center justify-between px-4 py-2.5 text-sm"
                data-testid={`rt-ais-weight-${row.key}`}
              >
                <span>{t(`research_transparency.ais_weight_${row.key}`)}</span>
                <span
                  className="font-mono tabular-nums text-foreground"
                  data-testid={`rt-ais-weight-${row.key}-pct`}
                >
                  {row.pct}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="rt-ais-completeness">
            {t("research_transparency.ais_completeness_note")}
          </p>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="rt-ais-guardrails">
            {t("research_transparency.ais_guardrails_note")}
          </p>
          {/* Sub-pass 2A (2026-02-17) — score/classification-are-
              separate-layers clarification.  Sits directly after the
              existing Opportunity Score + guardrail explanation
              where it fits naturally, and states in plain language
              that a high score alone never guarantees Opportunity /
              Top Opportunity because the classification also
              considers valuation, upside, risk, data quality and
              deterministic guardrails. */}
          <p
            className="mt-4 text-sm text-muted-foreground leading-relaxed"
            data-testid="rt-ais-score-vs-classification"
          >
            {t("research_transparency.ais_score_vs_classification")}
          </p>
        </Section>

        {/* 4. What AI does — and does not do */}
        <Section id="role-of-ai" title={t("research_transparency.section_role_of_ai")}>
          <SubHead>{t("research_transparency.ai_deterministic_title")}</SubHead>
          <p data-testid="rt-ai-deterministic">{t("research_transparency.ai_deterministic_body")}</p>

          <SubHead>{t("research_transparency.ai_llm_title")}</SubHead>
          <p data-testid="rt-ai-llm">{t("research_transparency.ai_llm_body")}</p>

          <SubHead>{t("research_transparency.ai_does_not_title")}</SubHead>
          <p data-testid="rt-ai-does-not">{t("research_transparency.ai_does_not_body")}</p>
        </Section>

        {/* 5. Data sources, timestamps and data quality */}
        <Section id="data-sources" title={t("research_transparency.section_data_sources")}>
          <p data-testid="rt-ds-intro">{t("research_transparency.ds_intro")}</p>
          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-4" data-testid="rt-ds-fields">
            {DATA_FIELDS.map((row) => (
              <div key={row.key} data-testid={`rt-ds-field-${row.key}`}>
                <dt className="text-sm font-medium text-foreground">
                  {t(`research_transparency.ds_field_${row.key}`)}
                </dt>
                <dd className="text-sm text-muted-foreground mt-0.5">
                  {t(`research_transparency.ds_field_${row.key}_desc`)}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-sm text-muted-foreground" data-testid="rt-ds-no-last-reviewed">
            {t("research_transparency.ds_no_last_reviewed_note")}
          </p>
        </Section>

        {/* 6. Assumptions, uncertainty and missing data */}
        <Section id="assumptions" title={t("research_transparency.section_assumptions")}>
          <p data-testid="rt-assump-intro">{t("research_transparency.assump_intro")}</p>

          <SubHead>{t("research_transparency.assump_fixed_title")}</SubHead>
          <ul className="list-disc pl-5 space-y-1.5 text-sm" data-testid="rt-assump-fixed">
            {FIXED_ASSUMPTIONS.map((k) => (
              <li key={k} data-testid={`rt-assump-${k}`}>{t(`research_transparency.${k}`)}</li>
            ))}
          </ul>

          <SubHead>{t("research_transparency.assump_dynamic_title")}</SubHead>
          <p data-testid="rt-assump-dynamic">{t("research_transparency.assump_dynamic_body")}</p>

          <SubHead>{t("research_transparency.assump_missing_title")}</SubHead>
          <p data-testid="rt-assump-missing">{t("research_transparency.assump_missing_body")}</p>
        </Section>

        {/* 7. Conflicts of interest */}
        <Section id="conflicts" title={t("research_transparency.section_conflicts")}>
          <p
            className="rounded-lg border border-border/60 bg-secondary/20 px-4 py-3 text-sm font-medium"
            data-testid="rt-conflicts-headline"
          >
            {conflictsLabel(t)}
          </p>

          <SubHead>{t("research_transparency.conflicts_business_fact_title")}</SubHead>
          <p data-testid="rt-conflicts-body">
            {t("research_transparency.conflicts_business_fact_body")}
          </p>

          <p className="mt-4 text-sm text-muted-foreground" data-testid="rt-conflicts-maintenance">
            {t("research_transparency.conflicts_maintenance_note")}
          </p>
        </Section>

        {/* 8. Corrections */}
        <Section id="corrections" title={t("research_transparency.section_corrections")}>
          <p data-testid="rt-corrections-intro">
            {t("research_transparency.corrections_intro")}
          </p>
          <SubHead>{t("research_transparency.corrections_current_state_title")}</SubHead>
          <p data-testid="rt-corrections-current-state">
            {t("research_transparency.corrections_current_state_body")}
          </p>
        </Section>

        {/* 9. Limitations + Financial Disclaimer link */}
        <Section id="limitations" title={t("research_transparency.section_limitations")}>
          <p data-testid="rt-limitations-body">
            {t("research_transparency.limitations_body")}
          </p>
          <div className="mt-4">
            <Disclaimer compact language={lang} />
          </div>
          <p className="mt-4">
            <Link
              to={disclaimerHref}
              className="text-[#3182CE] hover:underline text-sm font-medium"
              data-testid="rt-limitations-disclaimer-link"
              data-rt-disclaimer-language={lang}
            >
              {t("research_transparency.limitations_link_disclaimer")} →
            </Link>
          </p>
        </Section>

        {/* Optional collapsed Technical details */}
        <details
          className="mt-8 rounded-xl border border-border/60 bg-secondary/10 px-4 py-3"
          data-testid="rt-tech-details"
        >
          <summary className="cursor-pointer text-sm font-medium text-muted-foreground select-none">
            {t("research_transparency.section_tech_details")}
          </summary>
          <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px] text-muted-foreground">
            <div>
              <dt className="font-medium text-foreground">
                {t("research_transparency.tech_model")}
              </dt>
              <dd data-testid="rt-tech-model">stockaily.deterministic_valuation.v1</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">
                {t("research_transparency.methodology_label")}
              </dt>
              <dd data-testid="rt-tech-methodology-id">{V1}</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">
                {t("research_transparency.tech_schema")}
              </dt>
              <dd data-testid="rt-tech-schema">ws7.metadata.v1</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">
                {t("research_transparency.tech_regcopy")}
              </dt>
              <dd data-testid="rt-tech-regcopy">ws1.v1</dd>
            </div>
          </dl>
          <p
            className="mt-3 text-[11.5px] text-muted-foreground/85 italic"
            data-testid="rt-tech-ai-assisted-note"
          >
            {t("research_transparency.ai_assisted_explanation")}
          </p>
        </details>
      </main>
      <Footer />
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section
      id={id}
      className="mt-10 sm:mt-12"
      data-testid={`rt-section-${id}`}
    >
      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
        {title}
      </h2>
      <div className="space-y-3 text-base leading-relaxed text-foreground/90">
        {children}
      </div>
    </section>
  );
}

function SubHead({ children }) {
  return (
    <h3 className="mt-5 mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  );
}
